import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useSelector } from "react-redux";
import {
  Avatar,
  Badge,
  Button,
  Image,
  Input,
  message as antMessage,
  Popconfirm,
  Spin,
  Tooltip,
  Upload,
} from "antd";
import {
  ArrowRightOutlined,
  AudioOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExpandOutlined,
  FileOutlined,
  MessageFilled,
  MessageOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RetweetOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ChatApi } from "../../services";
import ChatEntityCard from "../ChatEntityCard";
import ChatEntityPicker from "../ChatEntityPicker";
import ChatInfoPanel from "../ChatInfoPanel";
import EmptyState from "../EmptyState";
import { useMediaQuery } from "../../hooks";
import chatSocket, { setActiveRoomId } from "../../utils/chatSocket";
import styles from "./styles.module.css";
import moment from "moment-jalaali";
import { uploadRejectionReason } from "../../utils/uploadGuard";

moment.loadPersian({ dialect: "persian-modern" });

const getRoomName = (room, uid) => {
  if (room.title) return room.title;
  const other = room.participants?.find((p) => p.id !== uid);
  return other?.full_name || "کاربر";
};

const getRoomAvatar = (room, uid) => {
  if (room.title) return null;
  const other = room.participants?.find((p) => p.id !== uid);
  return other?.avatar?.original_url || null;
};

const fmtTime = (date) => (date ? moment(date).format("HH:mm") : "");
const fmtDateSep = (date) =>
  date ? moment(date).format("jYYYY/jMM/jDD") : "";
const isSameDay = (a, b) =>
  moment(a).format("jYYYY-jMM-jDD") === moment(b).format("jYYYY-jMM-jDD");

const fmtPlayer = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const detectType = (name = "") => ({
  isImg: /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name),
  isVid: /\.(mp4|mov|webm|avi|mkv)$/i.test(name),
  isAud: /\.(ogg|mp3|wav|m4a|aac|opus)$/i.test(name),
});

const AudioPlayer = ({ src, isOwn }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  return (
    <div
      className={`${styles.audioPlayer} ${isOwn ? styles.audioPlayerOwn : styles.audioPlayerOther}`}
    >
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => {
          const a = audioRef.current;
          if (!a?.duration) return;
          setCurrentTime(a.currentTime);
          setProgress((a.currentTime / a.duration) * 100);
        }}
        onLoadedMetadata={() => {
          const d = audioRef.current?.duration;
          if (d && isFinite(d)) setDuration(d);
        }}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
          setCurrentTime(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
        }}
      />
      <button className={styles.audioPlayBtn} onClick={toggle} type="button">
        {playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
      </button>
      <div className={styles.audioBody}>
        <div
          className={styles.audioProgressWrap}
          onClick={(e) => {
            const a = audioRef.current;
            if (!a?.duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = Math.max(
              0,
              Math.min(1, (e.clientX - rect.left) / rect.width)
            );
            a.currentTime = pct * a.duration;
          }}
        >
          <div className={styles.audioProgressBg} />
          <div
            className={styles.audioProgressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className={styles.audioTimes}>
          <span>{fmtPlayer(currentTime)}</span>
          <span>{fmtPlayer(duration)}</span>
        </div>
      </div>
    </div>
  );
};

const VideoPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      v.play();
      setPlaying(true);
    }
  };

  return (
    <div className={styles.videoContainer}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        className={styles.videoEl}
        onEnded={() => setPlaying(false)}
        onClick={toggle}
        playsInline
        controls={playing}
      />
      {!playing && (
        <div className={styles.videoOverlay} onClick={toggle}>
          <PlayCircleOutlined className={styles.videoPlayIcon} />
        </div>
      )}
    </div>
  );
};

const AttachmentItem = ({ att, isOwn }) => {
  const url = att.original_url || att.url || "";
  const name = att.file_name || att.name || "فایل";
  const { isImg, isVid, isAud } = (() => {
    const t = detectType(url.split("?")[0]);
    if (t.isImg || t.isVid || t.isAud) return t;
    return detectType(name);
  })();

  if (isImg) {
    return (
      <Image
        src={url}
        alt={name}
        width={200}
        style={{
          borderRadius: 8,
          maxHeight: 180,
          objectFit: "cover",
          display: "block",
          marginTop: 4,
        }}
        preview={{ mask: "🔍", zIndex: 10050 }}
      />
    );
  }
  if (isVid) return <VideoPlayer src={url} />;
  if (isAud) return <AudioPlayer src={url} isOwn={isOwn} />;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.attachFile}
      download
    >
      <FileOutlined />
      <span className={styles.attachFileName}>{name}</span>
      <DownloadOutlined />
    </a>
  );
};

const MessageRow = ({ msg, isOwn, onReply, onDelete, deleteLoading }) => {
  const [hovered, setHovered] = useState(false);
  const attachments = msg.attachments || [];
  const senderName =
    msg.sender?.full_name ||
    `${msg.sender?.first_name || ""} ${msg.sender?.last_name || ""}`.trim();

  return (
    <div
      className={`${styles.msgRow} ${isOwn ? styles.msgRowOwn : styles.msgRowOther}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={styles.msgContent}>
        <div
          className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : styles.bubbleOther}`}
          dir="rtl"
        >
          {!isOwn && senderName && (
            <span className={styles.msgSenderName}>{senderName}</span>
          )}

          {msg.reply_to && (
            <div className={styles.replyBadge}>
              <span className={styles.replyBadgeSender}>
                {msg.reply_to.sender?.full_name ||
                  `${msg.reply_to.sender?.first_name || ""} ${msg.reply_to.sender?.last_name || ""}`.trim()}
              </span>
              <span className={styles.replyBadgeText}>
                {msg.reply_to.message ||
                  (msg.reply_to.attachments?.length ? "📎 فایل" : "—")}
              </span>
            </div>
          )}

          {msg.message && <p className={styles.bubbleText}>{msg.message}</p>}

          {msg.entity && (
            <div className={styles.msgEntity}>
              <ChatEntityCard entity={msg.entity} compact />
            </div>
          )}

          {attachments.map((att, i) => (
            <AttachmentItem key={i} att={att} isOwn={isOwn} />
          ))}

          <span className={styles.bubbleTime}>{fmtTime(msg.created_at)}</span>
        </div>

        <div
          className={styles.msgActions}
          style={{ opacity: hovered ? 1 : 0 }}
        >
          <Tooltip title="پاسخ">
            <Button
              type="text"
              size="small"
              icon={<RetweetOutlined />}
              onClick={onReply}
              className={styles.msgActionBtn}
            />
          </Tooltip>
          {isOwn && (
            <Popconfirm
              title="حذف پیام"
              description="این پیام حذف شود؟"
              okText="بله"
              cancelText="خیر"
              onConfirm={onDelete}
            >
              <Tooltip title="حذف">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteLoading}
                  className={styles.msgActionBtn}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      </div>
    </div>
  );
};

export default function FloatChat() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const currentUser = useSelector(({ auth }) => auth.user);
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [sharedEntity, setSharedEntity] = useState(null);
  const [entityPickerOpen, setEntityPickerOpen] = useState(false);
  const [attachPreviews, setAttachPreviews] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSendingVoice, setIsSendingVoice] = useState(false);

  const panelRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);
  const prevScrollHeight = useRef(0);
  const isLoadingOlderRef = useRef(false);
  const needsInitialScroll = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const { data: rooms = [] } = useQuery({
    queryKey: ["[List] ChatRooms"],
    queryFn: () =>
      ChatApi.getChatRooms().then(
        (r) => r.data?.chat_rooms || r.data?.data || []
      ),
  });

  const {
    data: msgData,
    isFetching: msgLoading,
    isFetchingNextPage: olderLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["[List] ChatMessages", activeRoom?.id],
    queryFn: ({ pageParam = 1 }) =>
      ChatApi.getChatMessages({
        params: { id: activeRoom.id, per_page: 20, page: pageParam },
      }).then((r) => ({
        messages: r.data?.messages || [],
        pagination: r?.pagination || {},
      })),
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: last } = lastPage.pagination;
      return currentPage < last ? currentPage + 1 : undefined;
    },
    enabled: !!activeRoom?.id && open,
  });

  const messages = useMemo(() => {
    if (!msgData) return [];
    const all = msgData.pages.flatMap((p) => p.messages);
    const unique = [...new Map(all.map((m) => [m.id, m])).values()];
    return unique.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }, [msgData]);

  const appendMessageToCache = useCallback(
    (roomId, msg) => {
      queryClient.setQueryData(["[List] ChatMessages", roomId], (old) => {
        if (!old?.pages?.length) return old;
        const allRealIds = new Set(
          old.pages.flatMap((p) =>
            p.messages
              .filter((m) => !String(m.id).startsWith("opt-"))
              .map((m) => m.id)
          )
        );
        if (allRealIds.has(msg.id)) return old;
        const pages = old.pages.map((p) => ({
          ...p,
          messages: p.messages.filter(
            (m) =>
              !(
                String(m.id).startsWith("opt-") &&
                m.sender_id === msg.sender_id
              )
          ),
        }));
        const newPages = [...pages];
        newPages[newPages.length - 1] = {
          ...newPages[newPages.length - 1],
          messages: [...newPages[newPages.length - 1].messages, msg],
        };
        return { ...old, pages: newPages };
      });
    },
    [queryClient]
  );

  const patchRoomLastMessage = useCallback(
    (msg) => {
      queryClient.setQueryData(["[List] ChatRooms"], (old) => {
        if (!old) return old;
        return old.map((room) =>
          room.id === msg.chat_room_id
            ? { ...room, last_message: msg }
            : room
        );
      });
    },
    [queryClient]
  );

  const removeOptimisticMessages = useCallback(
    (roomId) => {
      queryClient.setQueryData(["[List] ChatMessages", roomId], (old) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            messages: p.messages.filter(
              (m) => !String(m.id).startsWith("opt-")
            ),
          })),
        };
      });
    },
    [queryClient]
  );

  useEffect(() => {
    if (!activeRoom?.id) return;
    setActiveRoomId(activeRoom.id);
    const roomId = activeRoom.id;
    chatSocket
      .private(`chat_rooms.${roomId}`)
      .listen(".new_chat_message", (data) => {
        const msg = data?.chatMessage;
        if (!msg) return;
        appendMessageToCache(roomId, msg);
        patchRoomLastMessage(msg);
      });
    return () => {
      setActiveRoomId(null);
      chatSocket.leave(`chat_rooms.${roomId}`);
    };
  }, [activeRoom?.id, appendMessageToCache, patchRoomLastMessage]);

  const sendMutation = useMutation(ChatApi.sendMessage, {
    onSuccess: () => setIsSendingVoice(false),
    onError: () => {
      setIsSendingVoice(false);
      removeOptimisticMessages(activeRoom?.id);
      antMessage.error("خطا در ارسال پیام");
    },
  });

  const deleteMutation = useMutation(ChatApi.deleteMessage, {
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        ["[List] ChatMessages", activeRoom?.id],
        (old) => {
          if (!old?.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              messages: p.messages.filter(
                (m) => m.id !== variables.params.messageId
              ),
            })),
          };
        }
      );
    },
    onError: () => antMessage.error("خطا در حذف پیام"),
  });

  useEffect(() => {
    needsInitialScroll.current = true;
    prevScrollHeight.current = 0;
    isLoadingOlderRef.current = false;
    setReplyTo(null);
    setMessageText("");
    setInfoOpen(false);
  }, [activeRoom?.id]);

  useEffect(() => {
    if (!messages.length) return;
    if (needsInitialScroll.current) {
      needsInitialScroll.current = false;
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      });
      return;
    }
    if (isLoadingOlderRef.current) return;
    const area = messagesAreaRef.current;
    if (!area) return;
    const isAtBottom =
      area.scrollHeight - area.scrollTop - area.clientHeight < 150;
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  useEffect(() => {
    if (!olderLoading && isLoadingOlderRef.current) {
      const saved = prevScrollHeight.current;
      isLoadingOlderRef.current = false;
      prevScrollHeight.current = 0;
      requestAnimationFrame(() => {
        if (messagesAreaRef.current && saved) {
          messagesAreaRef.current.scrollTop =
            messagesAreaRef.current.scrollHeight - saved;
        }
      });
    }
  }, [olderLoading]);

  const handleMessagesScroll = useCallback(
    (e) => {
      const { scrollTop } = e.target;
      if (
        scrollTop < 80 &&
        !olderLoading &&
        hasNextPage &&
        !isLoadingOlderRef.current
      ) {
        prevScrollHeight.current = e.target.scrollHeight;
        isLoadingOlderRef.current = true;
        fetchNextPage();
      }
    },
    [olderLoading, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (activeRoom && open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [activeRoom?.id, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!panelRef.current || panelRef.current.contains(e.target)) return;

      if (
        e.target.closest?.(
          ".ant-image-preview-root, .ant-image-preview-wrap, .ant-image-preview-mask, .ant-image-preview-operations, .ant-popover, .ant-modal-root, .ant-modal-mask, .ant-tooltip, .ant-upload"
        )
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    return () => {
      clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
        mediaRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const addAttachment = useCallback((file) => {
    const { isImg, isVid, isAud } = detectType(file.name);
    const url = isImg || isVid || isAud ? URL.createObjectURL(file) : null;
    setAttachments((prev) => [...prev, file]);
    setAttachPreviews((prev) => [
      ...prev,
      { url, isImg, isVid, isAud, name: file.name },
    ]);
  }, []);

  const removeAttachment = useCallback((i) => {
    setAttachments((prev) => prev.filter((_, j) => j !== i));
    setAttachPreviews((prev) => {
      if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, j) => j !== i);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachPreviews((prev) => {
      prev.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      return [];
    });
    setAttachments([]);
  }, []);

  const handleSend = useCallback(() => {
    const text = messageText.trim();
    if (!text && !attachments.length && !sharedEntity) return;
    if (!activeRoom) return;

    const currentReplyTo = replyTo;
    const currentAttachments = [...attachments];
    const currentEntity = sharedEntity;

    setMessageText("");
    setReplyTo(null);
    setSharedEntity(null);
    clearAttachments();

    if ((text || currentEntity) && !currentAttachments.length) {
      const optimisticMsg = {
        id: `opt-${Date.now()}`,
        chat_room_id: activeRoom.id,
        sender_id: currentUser?.id,
        message: text,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        reply_to_id: currentReplyTo?.id || null,
        reply_to: currentReplyTo || null,
        entity: currentEntity || null,
        attachments: [],
        sender: currentUser,
      };
      queryClient.setQueryData(
        ["[List] ChatMessages", activeRoom.id],
        (old) => {
          if (!old?.pages?.length) return old;
          const pages = [...old.pages];
          pages[pages.length - 1] = {
            ...pages[pages.length - 1],
            messages: [...pages[pages.length - 1].messages, optimisticMsg],
          };
          return { ...old, pages };
        }
      );
    }

    sendMutation.mutate({
      body: {
        chat_room_id: activeRoom.id,
        message: text,
        reply_to_id: currentReplyTo?.id || undefined,
        entity_type: currentEntity?.type,
        entity_id: currentEntity?.id,
        attachments: currentAttachments,
      },
    });
  }, [
    messageText,
    attachments,
    sharedEntity,
    activeRoom,
    replyTo,
    currentUser,
    sendMutation,
    clearAttachments,
    queryClient,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const beforeUpload = (file) => {
    /* Mirrors the API's upload rule so the answer arrives before the bytes do
       — see src/utils/uploadGuard.js. The server still decides. */
    const reason = uploadRejectionReason(file);
    if (reason) {
      antMessage.error(reason);
      return false;
    }
    addAttachment(file);
    return false;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: "audio/ogg; codecs=opus",
        });
        const file = new File([blob], `voice-${Date.now()}.ogg`, {
          type: "audio/ogg",
        });
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        setRecordingSeconds(0);
        setIsSendingVoice(true);
        sendMutation.mutate({
          body: {
            chat_room_id: activeRoom?.id,
            message: "پیام صوتی",
            attachments: [file],
          },
        });
      };
      recorder.start();
      setIsRecording(true);
      recordingTimerRef.current = setInterval(
        () => setRecordingSeconds((s) => s + 1),
        1000
      );
    } catch {
      antMessage.error("دسترسی به میکروفون امکان‌پذیر نیست");
    }
  };

  const stopRecording = () => {
    clearInterval(recordingTimerRef.current);
    mediaRecorderRef.current?.stop();
  };

  const cancelRecording = () => {
    clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {}
      mediaRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
    }
    setIsRecording(false);
    setRecordingSeconds(0);
  };

  const fmtRecTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (pathname === "/chat") return null;

  const totalUnread = rooms.reduce((s, r) => s + (r.unread_count || 0), 0);
  const recentRooms = [...rooms]
    .sort(
      (a, b) =>
        new Date(b.last_message?.created_at || b.created_at || 0) -
        new Date(a.last_message?.created_at || a.created_at || 0)
    )
    .slice(0, 8);

  const expandToFullChat = () => {
    if (!activeRoom) return;
    setOpen(false);
    navigate(`/chat?room=${activeRoom.id}`);
  };

  return (
    <div className={styles.wrap} ref={panelRef}>
      <div className={`${styles.panel} ${open ? styles.panelVisible : ""}`}>
        {!activeRoom ? (
          <>
            <div className={styles.header}>
              <span className={styles.headerTitle}>
                <MessageOutlined />
                پیام‌ها
              </span>
              <button
                className={styles.headerClose}
                onClick={() => setOpen(false)}
                aria-label="بستن"
              >
                <CloseOutlined />
              </button>
            </div>

            <div className={`${styles.list} ds-scroll-compact`}>
              {recentRooms.length === 0 ? (
                <div className={styles.empty}>گفتگویی وجود ندارد</div>
              ) : (
                recentRooms.map((room) => {
                  const name = getRoomName(room, currentUser?.id);
                  const avatar = getRoomAvatar(room, currentUser?.id);
                  const unread = room.unread_count || 0;
                  const isGroup = !!room.title;
                  return (
                    <button
                      key={room.id}
                      className={styles.roomItem}
                      onClick={() => setActiveRoom(room)}
                    >
                      <Badge count={unread} size="small" offset={[-3, 3]}>
                        <Avatar
                          size={40}
                          src={avatar}
                          icon={isGroup ? <TeamOutlined /> : <UserOutlined />}
                          className={isGroup ? styles.groupAvatar : ""}
                        />
                      </Badge>
                      <div className={styles.roomMeta}>
                        <div className={styles.roomRow}>
                          <span className={styles.roomName}>{name}</span>
                          {room.last_message?.created_at && (
                            <span className={styles.roomTime}>
                              {fmtTime(room.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        {room.last_message?.message && (
                          <span
                            className={`${styles.roomPreview} ${unread ? styles.roomPreviewBold : ""}`}
                          >
                            {room.last_message.message}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className={styles.footer}>
              <button
                className={styles.footerBtn}
                onClick={() => {
                  setOpen(false);
                  navigate("/chat");
                }}
              >
                مشاهده همه پیام‌ها
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.chatHeader}>
              <button
                className={styles.backBtn}
                onClick={() => setActiveRoom(null)}
                aria-label="بازگشت"
              >
                <ArrowRightOutlined />
              </button>
              <div
                className={styles.chatHeaderClickable}
                onClick={() => setInfoOpen(true)}
                title="نمایش اطلاعات"
              >
                <Avatar
                  size={34}
                  src={getRoomAvatar(activeRoom, currentUser?.id)}
                  icon={activeRoom.title ? <TeamOutlined /> : <UserOutlined />}
                  className={activeRoom.title ? styles.groupAvatar : ""}
                />
                <div className={styles.chatHeaderInfo}>
                  <span className={styles.chatHeaderName}>
                    {getRoomName(activeRoom, currentUser?.id)}
                  </span>
                  {activeRoom.title && (
                    <span className={styles.chatHeaderSub}>
                      {activeRoom.participants?.length ?? "?"} عضو
                    </span>
                  )}
                </div>
              </div>
              <Tooltip title="باز کردن کامل">
                <button
                  className={styles.expandBtn}
                  onClick={expandToFullChat}
                  aria-label="باز کردن کامل"
                >
                  <ExpandOutlined />
                </button>
              </Tooltip>
            </div>

            <div
              className={`${styles.messages} ds-scroll-compact`}
              ref={messagesAreaRef}
              onScroll={handleMessagesScroll}
            >
              {olderLoading && (
                <div className={styles.olderLoadingWrap}>
                  <Spin size="small" />
                </div>
              )}
              {msgLoading && !messages.length ? (
                <div className={styles.msgLoading}>
                  <Spin size="small" />
                </div>
              ) : messages.length === 0 ? (
                <div className={styles.msgEmpty}>
                  <EmptyState compact title="هنوز پیامی نیست" description="اولین پیام رو بفرست." />
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isOwn =
                    msg.sender_id === currentUser?.id ||
                    msg.sender?.id === currentUser?.id;
                  const showDateSep =
                    idx === 0 ||
                    !isSameDay(messages[idx - 1].created_at, msg.created_at);
                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSep && (
                        <div className={styles.dateSep}>
                          <span className={styles.dateSepText}>
                            {fmtDateSep(msg.created_at)}
                          </span>
                        </div>
                      )}
                      <MessageRow
                        msg={msg}
                        isOwn={isOwn}
                        onReply={() => {
                          setReplyTo(msg);
                          inputRef.current?.focus();
                        }}
                        onDelete={() =>
                          deleteMutation.mutate({
                            params: {
                              chatId: activeRoom.id,
                              messageId: msg.id,
                            },
                          })
                        }
                        deleteLoading={deleteMutation.isLoading}
                      />
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className={styles.inputArea}>
              {replyTo && (
                <div className={styles.replyPreview}>
                  <RetweetOutlined className={styles.replyIcon} />
                  <span className={styles.replyText}>
                    <strong>
                      {replyTo.sender?.full_name ||
                        `${replyTo.sender?.first_name || ""} ${replyTo.sender?.last_name || ""}`.trim()}
                    </strong>
                    : {replyTo.message || (replyTo.attachments?.length ? "📎 فایل" : "")}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setReplyTo(null)}
                  />
                </div>
              )}

              {sharedEntity && (
                <div className={styles.entityPreview}>
                  <ChatEntityCard
                    entity={sharedEntity}
                    compact
                    asLink={false}
                    onRemove={() => setSharedEntity(null)}
                  />
                </div>
              )}

              {attachPreviews.length > 0 && (
                <div className={styles.attachPreview}>
                  {attachPreviews.map((p, i) => (
                    <div key={i} className={styles.attachChipMedia}>
                      {p.isImg && (
                        <img
                          src={p.url}
                          alt={p.name}
                          className={styles.attachThumb}
                        />
                      )}
                      {p.isVid && (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video src={p.url} className={styles.attachThumb} />
                      )}
                      {p.isAud && (
                        <div className={styles.attachAudioChip}>
                          <AudioOutlined />
                        </div>
                      )}
                      {!p.isImg && !p.isVid && !p.isAud && (
                        <>
                          <FileOutlined />
                          <span className={styles.attachChipName}>
                            {p.name}
                          </span>
                        </>
                      )}
                      <button
                        className={styles.attachRemoveBtn}
                        onClick={() => removeAttachment(i)}
                        aria-label="حذف"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.inputRow}>
                {isSendingVoice ? (
                  <div className={styles.voiceSendingRow}>
                    <Spin size="small" />
                    <span>در حال ارسال...</span>
                  </div>
                ) : isRecording ? (
                  <>
                    <Tooltip title="لغو">
                      <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={cancelRecording}
                      />
                    </Tooltip>
                    <div className={styles.recordingIndicator}>
                      <span className={styles.recordingDot} />
                      <span>{fmtRecTime(recordingSeconds)}</span>
                    </div>
                    <Tooltip title="ارسال صدا">
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        style={{ transform: "rotate(180deg)" }}
                        shape="circle"
                        onClick={stopRecording}
                      />
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Upload
                      multiple
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      accept="*/*"
                    >
                      <Tooltip title="پیوست فایل">
                        <Button type="text" icon={<PaperClipOutlined />} />
                      </Tooltip>
                    </Upload>

                    <Tooltip title="اشتراک‌گذاری تسک، پروژه یا نامه">
                      <Button
                        type="text"
                        icon={<LinkOutlined />}
                        onClick={() => setEntityPickerOpen(true)}
                      />
                    </Tooltip>

                    <Input.TextArea
                      ref={inputRef}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="پیام بنویسید..."
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      className={styles.msgInput}
                      variant="filled"
                      dir="rtl"
                    />

                    {messageText.trim() || attachments.length || sharedEntity ? (
                      <Tooltip title="ارسال">
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          shape="circle"
                          style={{ transform: "rotate(180deg)" }}
                          onClick={handleSend}
                          loading={sendMutation.isLoading}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="ضبط صدا">
                        <Button
                          type="text"
                          icon={<AudioOutlined />}
                          shape="circle"
                          onClick={startRecording}
                        />
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            </div>

            {}
            <ChatInfoPanel
              open={infoOpen}
              onClose={() => setInfoOpen(false)}
              room={activeRoom}
              currentUserId={currentUser?.id}
              onRoomUpdated={(room) => setActiveRoom(room)}
              compact
            />
          </>
        )}
      </div>

      <button
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => {
          if (isMobile) {
            navigate("/chat");
            return;
          }
          setOpen((v) => !v);
        }}
        aria-label="پیام‌ها"
      >
        <span className={styles.triggerInner}>
          {open ? (
            <CloseOutlined style={{ fontSize: 20 }} />
          ) : (
            <>
              <MessageFilled style={{ fontSize: 22 }} />
              {totalUnread > 0 && (
                <span className={styles.badge}>
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </>
          )}
        </span>
      </button>

      <ChatEntityPicker
        open={entityPickerOpen}
        onClose={() => setEntityPickerOpen(false)}
        onPick={setSharedEntity}
      />
    </div>
  );
}
